<script lang="ts">
import { Options } from 'vue-class-component';
import { Prop } from '@/helpers/Decorators';
import { HistoryItemModel } from "@/modules/logito/office/services/OfficeDocuments";
import { invoiceStatusVariant, historyStatusVariant } from '@/modules/low-code/inc/finance/helpers';
import Pager from '@/helpers/Pager';

import LoaderMixin from "@/modules/logito/office/components/loaderMixin";
import EntityService from '@/modules/common/services/EntityService';
import { ParticipantModel } from "@/modules/logito/office/services/RegisterService";

import UserAvatar from '@/components/common/UserAvatar.vue';
import Participant from "@/modules/low-code/components/Participant.vue";
import Substitute from '@/modules/core/substitutions/views/Substitute.vue';
import FileList from '@/components/common/FileList.vue';
import ArchiveDataModal from '@/modules/low-code/components/ArchiveDataModal.vue';
import TaskErrorModal from '@/components/common/TaskErrorModal.vue';
import IdeoTooltip from '@/components/ideo/tooltip/IdeoTooltip.vue';

@Options({
    components: { UserAvatar, Participant, Substitute, FileList, ArchiveDataModal, TaskErrorModal, IdeoTooltip },
})
export default class DocumentHistory extends LoaderMixin
{
    public historyPagerCount = 20;
    @Prop({ default: '', required: true }) public publicId: string;
    @Prop({ default: '', required: true }) public type: string;
    @Prop({ default: false }) public design: boolean;
    @Prop({ default: "" }) public blueprintName: string;
    @Prop({ default: false }) public isAdmin: boolean;

    public history: HistoryItemModel[] = [];
    public pager = new Pager(1, this.historyPagerCount, "DateCreatedUtc", "desc");
    public showErrorModal = false;

    public async created(): Promise<void>
    {
        this.pager.setTotalRows(0);
        !this.design && (await this.init());
    }

    public mounted(): void
    {
        if (this.design) return;

        this.$events.$on('refetchHistory', this.fetchData);
    }

    public unmounted(): void
    {
        if (this.design) return;

        this.$events.$off('refetchHistory', this.fetchData);
    }

    public invoiceStatusVariant = invoiceStatusVariant;
    public historyStatusVariant = historyStatusVariant;

    public getElements(targets: ParticipantModel[], visible: boolean = true): ParticipantModel[]
    {
        return visible ? targets.slice(0, 5) : targets.slice(5);
    }

    public showInfo(id: string): void
    {
        this.$events.$emit('fetchUser', { id });
    }

    public async showTaskErrors(historyId: number): Promise<void>
    {
        const modal = this.$refs.taskErrorModal as any;

        if (modal && modal.openWithHistoryId)
        {
            await modal.openWithHistoryId(historyId);
        }
    }

    public isNotAllHistory(): boolean
    {
        return this.pager.getTotalRows() > this.historyPagerCount;
    }

    public getHistoryCounter(): string
    {
        if (this.isNotAllHistory())
            return this.historyPagerCount + "+";

        return this.pager.getTotalRows().toString();
    }

    public async getAllHistory(): Promise<void>
    {
        this.historyPagerCount = 999999;
        this.pager.setPageSize(this.historyPagerCount);
        await this.fetchData();
    }

    public async fetchData(): Promise<void>
    {
        try
        {
            const response = await EntityService.getEntityHistory(this.publicId, this.type, this.pager);
            const items = response.items ?? [];
            const totalRows = response.totalRows ?? items.length;

            this.pager.setTotalRows(totalRows);
            this.history = items.map((item) => item.result);
        }
        catch (ex)
        {
            this.handleException(ex, {
                0: () => this.$alert.danger(this.$t('[[[Nie udało się pobrać historii]]]')),
            });
        }
    }
}
</script>

<template>
    <div>
        <ListView :items="history" :preload="false" class="changelog-table">
            <template #row="{ item, index }">
                <ListViewItem :title="$t('[[[Pracownik]]]')" width="250">
                    <div class="d-flex align-items-center">
                        <UserAvatar
                            :src="$filters.baseurl(`storage/files/${item.created?.user?.pictureUrl}`)"
                            :username="item?.created?.user?.value"
                            :key="item?.created?.user?.pictureUrl"
                            class="cursor-pointer"
                            @click="showInfo(item?.created?.user?.key)"
                        />
                        <Substitute
                            v-if="item?.created?.impersonator?.key"
                            :user="item?.created?.impersonator?.value"
                        />
                        <div class="ms-2">
                            <p class="mb-0">
                                <strong class="text-primary cursor-pointer" @click="showInfo(item?.created?.user?.key)">
                                    <span>{{ item?.created?.user?.value }}</span>
                                </strong>
                            </p>
                            <p
                                class="mb-1 text-sm"
                                v-if="item.action && item.action.value"
                                :variant="invoiceStatusVariant(item.action.key)"
                            >
                                {{ item.action.value }}
                                <IdeoBadge
                                    v-if="item.executeStatus && item.executeStatus.value"
                                    :variant="historyStatusVariant(item.executeStatus.key)"
                                    size="lg"
                                >
                                    {{ item.executeStatus.value }}
                                </IdeoBadge>
                            </p>
                        </div>
                    </div>
                </ListViewItem>
                <ListViewHeader :title="$t('[[[Data]]]')" width="170">
                    {{ $filters.datetime(item.created.dateUtc) }}
                </ListViewHeader>
                <ListViewItem :title="$t('[[[Kolejne osoby w procesie]]]')" width="200">
                    <div class="participant d-flex">
                        <Participant
                            v-for="(user, userIndex) in getElements(item.targets)"
                            :key="userIndex"
                            :user="user"
                            :index="'document-history-' + index + userIndex"
                            :is-history-mode="true"
                            :licence="type"
                            :document-id="publicId"
                            :history-id="item.id"
                        />
                        <IdeoDropdown
                            v-if="getElements(item.targets, false).length"
                            boundary="body"
                            variant="secondary"
                            menu-class="scroll"
                            :top-offset="5"
                            no-caret
                        >
                            <template #button-content>
                                <i class="fas fa-plus me-1"></i>
                                <span>{{ getElements(item.targets, false).length }}</span>
                            </template>
                            <template #default>
                                <div
                                    v-for="(user, userIndex) in getElements(item.targets, false)"
                                    :key="userIndex"
                                    class="p-2 d-flex align-items-center"
                                >
                                    <Participant
                                        :user="user"
                                        :index="'document-history-' + index + userIndex"
                                    />
                                    <p class="mb-0">
                                        <strong class="text-primary cursor-pointer">
                                            <span>{{ user?.user?.value }}</span>
                                        </strong>
                                    </p>
                                </div>
                            </template>
                        </IdeoDropdown>
                    </div>
                </ListViewItem>
                <ListViewItem :title="$t('[[[Uwagi]]]')" class="text-wrap">
                    {{ item.comment }}
                    <template v-if="item.attachements">
                        <FileList :files="item.attachements" :is-document="true" />
                    </template>
                </ListViewItem>
                <ListViewButtons width="50" icon="fas fa-magnifying-glass" mode="secondary">
                    <div class="position-relative">
                        <IdeoTooltip
                            v-if="item.taskError"
                            :tooltip="$t('[[[Podczas wykonywania skryptu lub service taska na tej akcji wystąpił błąd.]]]')"
                            position="left"
                            multiline
                        >
                            <i
                                class="fas fa-triangle-exclamation text-danger position-absolute"
                                :class="{ 'cursor-pointer': isAdmin, 'cursor-default': !isAdmin }"
                                style="opacity: 0.6; font-size: 1.5rem; left: -2.0rem; top: 0; height: 100%; line-height: 2.4rem; z-index: 1;"
                                @click="isAdmin && showTaskErrors(item.id)"
                            ></i>
                        </IdeoTooltip>
                        <IdeoButton v-if="item.hasArchiveData" icon="fa-solid fa-magnifying-glass" @click="$events.$emit('archive-data-modal', item.id, item.modalDisplaySize)" />
                    </div>
                </ListViewButtons>
            </template>
        </ListView>
        <div class="d-flex justify-content-center col-12" v-if="isNotAllHistory()">
            <ideo-button variant="primary" size="sm" class="rounded mt-3" @click="getAllHistory()">
                <i class="fa fa-plus me-1"></i>
                {{ $t("[[[Wyświetl wszystko]]]") }}
            </ideo-button>
        </div>
        <ArchiveDataModal :document-id="publicId" :licence="type" />
        <portal v-if="blueprintName" :to="`tab-counter-${blueprintName}`">
            {{ getHistoryCounter() }}
        </portal>
        <TaskErrorModal
            ref="taskErrorModal"
            v-model="showErrorModal"
        />
    </div>
</template>
